import { useEffect, useState } from "react";
import { usePageSEO } from "../hooks/usePageSEO";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, formatApiError } from "../lib/auth";
import { LOGO_URL } from "../lib/brand";
import { AlertCircle, Eye, EyeOff, Loader2, LogIn, X, Send } from "lucide-react";
import { MailDeliveryHint } from "../components/MailDeliveryHint";
import { log } from "../lib/log";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  usePageSEO({ title: "Login · Alphalane", noIndex: true });

  // Auto-redirect if already logged in
  useEffect(() => {
    if (user && user.id) {
      const target = location.state?.from || "/onboarding";
      navigate(target, { replace: true });
    }
  }, [user, navigate, location]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(form.email, form.password);
      toast.success("Eingeloggt!");
      navigate(location.state?.from || "/onboarding", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Pending customers receive a structured detail with a contract_token —
      // redirect them directly to the Vertrags-Step.
      if (detail && typeof detail === "object" && detail.contract_token) {
        try {
          localStorage.setItem("alphalane_contract_token", detail.contract_token);
        } catch (lsErr) {
          log.warn("[login] contract_token persist failed:", lsErr);
        }
        if (detail.contract_signed) {
          toast("Dein Vertrag ist eingegangen — wir prüfen ihn gerade.");
        } else {
          toast.info("Bitte unterschreibe zunächst den Dienstleistungsvertrag.");
        }
        navigate(`/onboarding/contract?token=${encodeURIComponent(detail.contract_token)}`);
        return;
      }
      setError(formatApiError(detail) || "Login fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)] flex flex-col" data-testid="login-page">
      <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
            <div className="font-display font-semibold text-[15px]"><span className="al-grad-text">ALPHALANE</span></div>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 sm:px-8 py-10">
        <div className="w-full max-w-md">
          <div className="al-card p-7 sm:p-9">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-xl al-grad-bg text-white flex items-center justify-center">
                <LogIn className="h-5 w-5" />
              </div>
              <div>
                <span className="al-tag">Login</span>
                <h1 className="mt-1 font-display font-bold text-2xl sm:text-3xl">Willkommen zurück.</h1>
              </div>
            </div>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
              <label className="block">
                <span className="text-xs uppercase tracking-widest font-semibold text-slate-500">E-Mail</span>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="login-email"
                  className="mt-1.5 w-full rounded-xl border border-[var(--al-line)] bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--al-purple)] focus:ring-2 focus:ring-[var(--al-purple)]/20"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest font-semibold text-slate-500">Passwort</span>
                <div className="mt-1.5 relative">
                  <input
                    type={showPw ? "text" : "password"}
                    required
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="login-password"
                    className="w-full rounded-xl border border-[var(--al-line)] bg-white px-4 py-2.5 pr-11 text-sm focus:outline-none focus:border-[var(--al-purple)] focus:ring-2 focus:ring-[var(--al-purple)]/20"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </label>

              {error && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-start gap-2" data-testid="login-error">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button type="submit" disabled={busy} className="al-cta w-full justify-center" data-testid="login-submit">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Logge ein…</> : <>Einloggen <LogIn className="h-4 w-4" /></>}
              </button>

              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={() => setShowForgot(true)}
                  data-testid="login-forgot-link"
                  className="text-sm text-[var(--al-purple)] hover:underline font-semibold"
                >
                  Passwort vergessen?
                </button>
              </div>
            </form>

            <div className="mt-6 pt-6 border-t border-[var(--al-line)] text-center text-sm text-slate-600">
              <div>
                Noch kein Konto? Bitte über deinen <strong>persönlichen Empfehlungslink</strong> registrieren.
              </div>
              <div className="mt-3 text-xs text-slate-500" data-testid="login-no-ref-help">
                Keinen Empfehlungslink?{" "}
                <Link
                  to="/register/alphalane-ai"
                  data-testid="login-register-moonshine"
                  className="text-[var(--al-purple)] font-semibold hover:underline"
                >
                  Direkt über Moonshine Solutions registrieren
                </Link>{" "}
                oder{" "}
                <a
                  href="mailto:kontakt@moonshine-solutions.com?subject=Registrierung%20Alphalane"
                  data-testid="login-contact-moonshine"
                  className="text-[var(--al-purple)] font-semibold hover:underline"
                >
                  kontaktiere uns
                </a>
                .
              </div>
            </div>
          </div>
        </div>
      </main>

      {showForgot && <ForgotPasswordModal initialEmail={form.email} onClose={() => setShowForgot(false)} />}
    </div>
  );
}

function ForgotPasswordModal({ initialEmail, onClose }) {
  const [email, setEmail] = useState(initialEmail || "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Anfrage fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center px-5" data-testid="forgot-modal">
      <div className="w-full max-w-md al-card p-7 sm:p-8 bg-white relative">
        <button onClick={onClose} className="absolute top-4 right-4 p-1 rounded-md hover:bg-slate-100 text-slate-400" data-testid="forgot-close" aria-label="Schließen">
          <X className="h-4 w-4" />
        </button>
        {sent ? (
          <div className="text-center" data-testid="forgot-sent">
            <div className="mx-auto h-12 w-12 rounded-full al-grad-bg text-white flex items-center justify-center mb-4">
              <Send className="h-5 w-5" />
            </div>
            <h2 className="font-display font-bold text-xl">Wir haben eine Mail an dich geschickt.</h2>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              Falls die E-Mail bei uns hinterlegt ist, findest du in deinem Postfach einen Reset-Link
              (gültig für 2 Stunden).
            </p>
            <MailDeliveryHint testid="forgot-mail-hint" />
            <button onClick={onClose} className="al-cta-ghost mt-6 w-full justify-center">Schließen</button>
          </div>
        ) : (
          <>
            <h2 className="font-display font-bold text-xl">Passwort vergessen?</h2>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              Trag einfach die E-Mail-Adresse deines Kontos ein. Wir schicken dir einen Reset-Link.
            </p>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <label className="block">
                <span className="text-xs uppercase tracking-widest font-semibold text-slate-500">E-Mail</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email"
                  className="mt-1.5 w-full rounded-xl border border-[var(--al-line)] bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--al-purple)] focus:ring-2 focus:ring-[var(--al-purple)]/20"
                />
              </label>
              <button type="submit" disabled={busy} className="al-cta w-full justify-center" data-testid="forgot-submit">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Sende…</> : <>Reset-Link anfordern <Send className="h-4 w-4" /></>}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
